import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Bell, WifiOff, RefreshCw, MessageSquare, ChevronRight, User, Check, Pencil, MapPin, ReceiptText, UtensilsCrossed, Users, Home as HomeIcon, BookOpen, ShoppingBag, X, Gamepad2, Wine, Info } from 'lucide-react';
import { apiService } from '@/services/api';
import MyBottlesModal from '@/components/BottleKeep/MyBottlesModal';
import { branchTypeTheme } from '@/utils/branchTypeTheme';
import { subscribeToChannel } from '@/services/ablyRealtime';
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import BranchBroadcasts from '@/components/BranchBroadcasts';
import CallStaff from '@/components/CallStaff/CallStaff';
import BookingChat from '@/components/BookingChat/BookingChat';
import MenuImagesModal from '@/components/MenuImages';
import TableServiceOrderModal from '@/components/TableServiceOrder/TableServiceOrderModal';
import { UiStyles, radius, shadow, brand } from '@/theme/ui';
import OrderTracker from '@/components/OrderTracker/OrderTracker';
import TableChat from '@/components/TableChat/TableChat';
import Concierge from '@/components/Concierge/Concierge';
import BillModal from '@/components/TableBill';
import BranchAbout from '@/components/BranchAbout';
import { beginGuestSessionClaim } from '@/utils/guestSessionClaim';

// Lazy so the games' code isn't in the main bundle (loads only when a guest opens the arcade).
const GamesHub = React.lazy(() => import('@/components/Game/GamesHub'));

/**
 * QrScanLanding — the public, no-login page a walk-in lands on after scanning a table QR
 * (`/t/:token`). It provisions a guest customer + auto walk-in check-in via the backend, stores the
 * returned session (so the app treats them as a normal customer), and drops them straight into the
 * "Call staff" screen for their table. No account, no typing — scan and call.
 *
 * Modeled on OAuthCallback: exchange the token → setTokens → land. Device-bound (a localStorage id)
 * so a re-scan on the same phone resumes the same guest. Provisioning is gated server-side, so an
 * off-hours / off-premise scan is refused with a friendly message.
 */
type Phase = 'loading' | 'ready' | 'unavailable' | 'error' | 'ended';

/** The per-check-in QR session id lives in the URL (?s=) so re-opening the link is recognised. */
const getUrlSid = (): string | undefined => {
  try { return new URLSearchParams(window.location.search).get('s') || undefined; } catch { return undefined; }
};
const putUrlSid = (sid: string): void => {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('s', sid);
    window.history.replaceState(null, '', url.toString());
  } catch { /* noop */ }
};

const getDeviceId = (): string => {
  try {
    let id = localStorage.getItem('qrDeviceId');
    if (!id) {
      id = (window.crypto?.randomUUID?.() || `dev_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`);
      localStorage.setItem('qrDeviceId', id);
    }
    return id;
  } catch {
    return `dev_${Math.random().toString(36).slice(2)}`;
  }
};

// "Regulars" opt-in token, stored per BRANCH so a phone can be a regular at several venues without
// one opt-in overwriting another. getScanInfo gives us branchId before we provision, so we can send
// the right token to be recognised on arrival.
const REGULAR_TOKENS_KEY = 'cu_regular_tokens';
const readRegularTokens = (): Record<string, string> => {
  try { return JSON.parse(localStorage.getItem(REGULAR_TOKENS_KEY) || '{}') || {}; } catch { return {}; }
};
const getRegularToken = (branchId?: string): string | undefined => (branchId ? readRegularTokens()[branchId] : undefined);
const saveRegularToken = (branchId: string, tok: string): void => {
  try { const m = readRegularTokens(); m[branchId] = tok; localStorage.setItem(REGULAR_TOKENS_KEY, JSON.stringify(m)); } catch { /* storage best-effort */ }
};

interface RegularInfo { name: string | null; visitCount: number; usualLabel: string | null; usualItems: Array<{ stockItemId?: string | null; itemName: string; quantity: number }> }

const ordinal = (n: number): string => {
  const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

/**
 * Category cover banner for the home hero — until venues can upload a real cover photo, we show a
 * tasteful gradient banner with emojis that reflect the venue TYPE (bar / restaurant / salon…), so it
 * reads like a Paytm-style hero image. When a real `coverUrl` exists it is used instead.
 */
const categoryHero = (bt?: { code?: string; label?: string } | null): { grad: string; emojis: string[] } => {
  const k = `${bt?.code || ''} ${bt?.label || ''}`.toLowerCase();
  const has = (...ws: string[]) => ws.some((w) => k.includes(w));
  if (has('salon', 'saloon', 'spa', 'barber', 'beauty')) return { grad: 'linear-gradient(135deg,#a78bfa,#f0abfc)', emojis: ['💇', '💅', '✂️'] };
  if (has('grocery', 'mart', 'super', 'kirana')) return { grad: 'linear-gradient(135deg,#34d399,#a7f3d0)', emojis: ['🛒', '🥕', '🧺'] };
  if (has('cafe', 'coffee', 'bakery', 'patisserie', 'tea')) return { grad: 'linear-gradient(135deg,#d6a35c,#f0d9b5)', emojis: ['☕', '🥐', '🍰'] };
  if (has('hotel', 'resort', 'suite', 'room')) return { grad: 'linear-gradient(135deg,#38bdf8,#a5f3fc)', emojis: ['🛎️', '🏨', '🥂'] };
  if (has('bar', 'pub', 'lounge', 'club', 'brew', 'billiard', 'pool')) return { grad: 'linear-gradient(135deg,#fb7185,#fdba74)', emojis: ['🍸', '🍺', '🍷'] };
  return { grad: 'linear-gradient(135deg,#FF8A5B,#FFC59B)', emojis: ['🍽️', '🍛', '🥘'] }; // restaurant / default food
};

const QrScanLanding: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [phase, setPhase] = useState<Phase>('loading');
  const [tableName, setTableName] = useState<string>('');
  const [branchName, setBranchName] = useState<string>('');
  const [organizerName, setOrganizerName] = useState<string>('');
  const [organizerLogo, setOrganizerLogo] = useState<string>('');
  const [bannerImage, setBannerImage] = useState<string>('');
  const [bookingId, setBookingId] = useState<string>('');
  // Offers live at this venue right now — shown so a guest knows what they're getting before ordering.
  const [offers, setOffers] = useState<any[]>([]);
  const [branchId, setBranchId] = useState<string>('');
  const [branchType, setBranchType] = useState<{ code?: string; label?: string } | null>(null);
  const [userId, setUserId] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [showCall, setShowCall] = useState(false);
  const [callOpenSongs, setCallOpenSongs] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [logoZoom, setLogoZoom] = useState(false); // tap the top-bar logo → view it big
  const [showChat, setShowChat] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showOrder, setShowOrder] = useState(false);
  const [showBill, setShowBill] = useState(false);
  const [billTotal, setBillTotal] = useState<number | null>(null); // live running-tab total for the glanceable Bill pill
  const [billCurrency, setBillCurrency] = useState('');
  const [showGame, setShowGame] = useState(false);
  const [showMyBottles, setShowMyBottles] = useState(false);
  const [myBottles, setMyBottles] = useState<any[]>([]);
  // Persist the "game is open" flag so a page refresh drops the guest back into the live game
  // (the game state itself lives on the server; the online board re-fetches it on mount).
  const openGame = () => { setShowGame(true); try { sessionStorage.setItem('cc_game_open', '1'); } catch { /* noop */ } };
  const closeGame = () => { setShowGame(false); try { sessionStorage.removeItem('cc_game_open'); } catch { /* noop */ } };
  // Once the session is ready (ids resolved), re-open the game if it was open before the refresh.
  useEffect(() => {
    if (phase === 'ready') { try { if (sessionStorage.getItem('cc_game_open') === '1') setShowGame(true); } catch { /* noop */ } }
  }, [phase]);
  // Bottle keep: load the guest's kept bottles so a "My bottles" tile appears when they have any.
  useEffect(() => {
    if (phase !== 'ready' || !bookingId) return;
    apiService.getMyKeptBottles(bookingId).then((list) => setMyBottles(list || [])).catch(() => { /* none */ });
  }, [phase, bookingId]);

  // Live offers for the badge/banner. Best-effort: a venue with none simply shows nothing.
  useEffect(() => {
    if (phase !== 'ready' || !branchId) return;
    apiService.getBranchOffers(branchId)
      .then((r: any) => setOffers(Array.isArray(r?.data) ? r.data : []))
      .catch(() => setOffers([]));
  }, [phase, branchId]);

  // Live running total for the "Bill" pill — refreshes on a timer + whenever the order/bill sheet closes.
  useEffect(() => {
    if (phase !== 'ready' || !bookingId) return;
    let alive = true;
    const load = () => apiService.getTableBill(bookingId).then((res: any) => {
      if (!alive) return;
      const d = res?.data ?? res;
      if (d) { setBillTotal(typeof d.grandTotal === 'number' ? d.grandTotal : (d.runningTab?.subtotal ?? null)); setBillCurrency(d.currency || ''); }
    }).catch(() => { /* no bill yet */ });
    load();
    const t = setInterval(load, 20000);
    return () => { alive = false; clearInterval(t); };
  }, [phase, bookingId, showBill, showOrder]);
  const [tableId, setTableId] = useState<string>('');
  const [currency, setCurrency] = useState<string>('');
  const [showTableChat, setShowTableChat] = useState(false);
  const [tableChatOn, setTableChatOn] = useState(false); // branch feature toggle
  const [bottleKeepOn, setBottleKeepOn] = useState(false); // branch feature toggle (bar-only)
  const [entertainmentPaused, setEntertainmentPaused] = useState(false); // Ramadan/seasonal mode
  // Number of menu photos this branch has. null = not loaded yet; the Menu tile shows only when > 0
  // so an empty menu doesn't offer a dead button.
  const [menuImageCount, setMenuImageCount] = useState<number | null>(null);
  const [tableChatUnread, setTableChatUnread] = useState(0);
  const [callUnread, setCallUnread] = useState(0); // unread staff replies while the Call staff panel is closed
  const showCallRef = useRef(false);
  useEffect(() => { showCallRef.current = showCall; }, [showCall]);
  const [attempt, setAttempt] = useState(0);
  // Hero carousel: venue slide (slide 0) + active events/offers pulled from the branch.
  type Highlight = { id: string; title: string; category: string; when: string | null; description: string | null; startsAt?: string | null; endsAt?: string | null; allDay?: boolean };
  const [highlights, setHighlights] = useState<Highlight[]>([]);
  const [slide, setSlide] = useState(0);
  // Optional guest display name — set/edited here, stored in qr_customer_profile.
  const [guestName, setGuestName] = useState<string>('');
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [savingName, setSavingName] = useState(false);
  // Regulars: recognised on scan (welcome-back + "the usual"), or opt-in for a first-timer.
  const [regular, setRegular] = useState<RegularInfo | null>(null);
  const [savingRegular, setSavingRegular] = useState(false);
  const [orderingUsual, setOrderingUsual] = useState(false);
  const [usualDone, setUsualDone] = useState(false);

  // Pull the venue's active events/offers for the hero carousel once we know the branch.
  useEffect(() => {
    if (!branchId) { setHighlights([]); return; }
    let cancelled = false;
    apiService.getBranchHighlights(branchId)
      .then((h) => { if (!cancelled) setHighlights(Array.isArray(h) ? h : []); })
      .catch(() => { if (!cancelled) setHighlights([]); });
    return () => { cancelled = true; };
  }, [branchId]);

  // Total hero slides = the venue slide + one per highlight. Keep the index in range as data loads.
  const slideCount = highlights.length + 1;
  useEffect(() => { if (slide >= slideCount) setSlide(0); }, [slideCount, slide]);
  // Gentle auto-advance (only when there's more than the venue slide, and the tab is visible).
  useEffect(() => {
    if (slideCount <= 1) return;
    const id = window.setInterval(() => {
      if (document.hidden) return;
      setSlide((s) => (s + 1) % slideCount);
    }, 6000);
    return () => window.clearInterval(id);
  }, [slideCount]);

  useEffect(() => {
    if (!token) { setPhase('error'); setMessage('Invalid QR code.'); return; }
    let cancelled = false;
    (async () => {
      setPhase('loading');
      try {
        // 1. Read venue/table info + availability for a fast, branded first paint.
        const infoRes = await apiService.getQrTable(token);
        const info = infoRes?.data || {};
        if (cancelled) return;
        setTableName(info.tableLabel || 'Your table');
        setBranchName(info.branchName || '');
        if (info.branchId) setBranchId(info.branchId);
        if (info.branchType) setBranchType({ code: info.branchType.code, label: info.branchType.label });
        if (info.organizerName) setOrganizerName(info.organizerName);
        if (info.organizerLogoUrl) setOrganizerLogo(info.organizerLogoUrl);
        if (info.bannerImage) setBannerImage(info.bannerImage);
        if (info.available === false) {
          setPhase('unavailable');
          setMessage('Table calling is not available right now. Please ask a staff member.');
          return;
        }
        // 2. Provision the guest + session (auto check-in) and log in. Carry the QR session id (?s=) so
        //    a checked-out / cleared / stale link is refused instead of starting a fresh session.
        const sesRes = await apiService.createQrSession(token, { deviceId: getDeviceId(), sessionId: getUrlSid(), rememberToken: getRegularToken(info.branchId) });
        const d = sesRes?.data || {};
        if (cancelled) return;
        if (!d.accessToken || !d.bookingId) throw new Error('Could not start your table session.');
        apiService.setGuestTokens(d.accessToken, d.refreshToken); // tab-scoped: see api.ts getTokens()
        // Persist the (new or resumed) session id into the URL so a re-open is recognised.
        if (d.sessionId) putUrlSid(d.sessionId);
        setBookingId(d.bookingId);
        if (d.tableId) setTableId(d.tableId);
        if (d.currency) setCurrency(d.currency);
        if (d.branchId || info.branchId) setBranchId(d.branchId || info.branchId);
        if (d.user?.id) setUserId(d.user.id);
        // Is guest-to-guest Table Chat enabled for this branch? (organizer can switch it off)
        apiService.getBranchFeatures(d.branchId || info.branchId)
          .then((fr: any) => { const feats = fr?.features || fr?.data?.features || []; const arr = Array.isArray(feats) ? feats : []; setTableChatOn(arr.includes('table_chat')); setBottleKeepOn(arr.includes('bottle_keep')); setEntertainmentPaused(fr?.ramadan?.pauseEntertainment === true); })
          .catch(() => {});
        // How many menu photos exist — so the Menu tile can hide itself when there's no menu to show.
        apiService.getBranchMenuImages(d.branchId || info.branchId)
          .then((mr: any) => { const imgs = mr?.images || mr?.data?.images || []; setMenuImageCount(Array.isArray(imgs) ? imgs.length : 0); })
          .catch(() => setMenuImageCount(0));
        setTableName(d.tableLabel || info.tableLabel || 'Your table');
        setBranchName(d.branchName || info.branchName || '');
        if (d.branchType || info.branchType) setBranchType({ code: (d.branchType || info.branchType).code, label: (d.branchType || info.branchType).label });
        if (d.organizerName || info.organizerName) setOrganizerName(d.organizerName || info.organizerName);
        if (d.organizerLogoUrl || info.organizerLogoUrl) setOrganizerLogo(d.organizerLogoUrl || info.organizerLogoUrl);
        // 'Guest' is the default placeholder — treat it as "no name set yet".
        const provided = d.user?.displayName && d.user.displayName !== 'Guest' ? d.user.displayName : '';
        setGuestName(provided);
        // Regulars: recognised returning guest → greet them + surface "the usual".
        if (d.regular) {
          setRegular(d.regular as RegularInfo);
          if ((d.regular as RegularInfo).name && !provided) setGuestName((d.regular as RegularInfo).name || '');
        }
        setPhase('ready');
      } catch (e) {
        if (cancelled) return;
        // A checked-out / cleared / stale QR link — show a clear "session ended" screen (not a retry error).
        const code = (e as any)?.code;
        const status = (e as any)?.status;
        if (code === 'QR_SESSION_ENDED' || status === 410) {
          setPhase('ended');
          setMessage(e instanceof Error ? e.message : 'Your table session has ended.');
          return;
        }
        setPhase('error');
        setMessage(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
      }
    })();
    return () => { cancelled = true; };
  }, [token, attempt]);

  /**
   * Re-establish this tab's table session and hand back the fresh bookingId.
   *
   * Used when an action is refused because the session drifted — the guest was checked out while the
   * sheet sat open, or (before guest tokens became tab-scoped) another tab overwrote the identity.
   * Silently re-provisioning beats showing a guest at a table an error they cannot act on.
   * Returns null if we genuinely can't recover, so the caller can surface a real message.
   */
  const reprovisionSession = useCallback(async (): Promise<string | null> => {
    if (!token) return null;
    try {
      const res = await apiService.createQrSession(token, { deviceId: getDeviceId(), sessionId: getUrlSid(), rememberToken: getRegularToken(branchId) });
      const d = res?.data || {};
      if (!d.accessToken || !d.bookingId) return null;
      apiService.setGuestTokens(d.accessToken, d.refreshToken);
      if (d.sessionId) putUrlSid(d.sessionId);
      setBookingId(d.bookingId);
      if (d.user?.id) setUserId(d.user.id);
      return d.bookingId as string;
    } catch {
      return null;
    }
  }, [token, branchId]);

  // Redirect to /login the INSTANT the organizer checks this guest out. The backend publishes
  // 'guest:session_ended' to this guest's own channel (user:{userId}); the auth middleware is still
  // the source of truth, but this makes the redirect immediate instead of waiting for a refresh.
  useEffect(() => {
    if (!userId || phase !== 'ready') return;
    const endSession = () => {
      try { apiService.clearTokens?.(); } catch { /* noop */ }
      // Checkout: wipe the Concierge conversation memory (it only lives for the visit).
      try { for (let i = localStorage.length - 1; i >= 0; i--) { const k = localStorage.key(i); if (k && k.startsWith('cc_chat_')) localStorage.removeItem(k); } } catch { /* noop */ }
      window.location.replace('/login');
    };
    let unsubscribe: (() => void) | undefined;
    try {
      unsubscribe = subscribeToChannel(`user:${userId}`, ['guest:session_ended'], () => endSession());
    } catch { /* realtime is best-effort; the middleware still ends access on next request */ }
    return () => { try { unsubscribe?.(); } catch { /* noop */ } };
  }, [userId, phase]);

  // Live unread badge on the "Call staff" button: staff replied in the request chat while the panel
  // is closed. Seed any counts already waiting, then keep them live over this guest's user channel.
  useEffect(() => {
    if (!userId || phase !== 'ready' || !bookingId) return;
    apiService.getServiceCalls()
      .then((res) => {
        const calls = ((res as any)?.data || []).filter((c: any) => c.bookingId === bookingId);
        const n = calls.reduce((s: number, c: any) => s + (c.unreadCount || 0), 0);
        if (n) setCallUnread(n);
      })
      .catch(() => { /* non-fatal */ });
    let unsub: (() => void) | undefined;
    try {
      unsub = subscribeToChannel(
        `user:${userId}`,
        ['service_ticket_message:new'] as unknown as readonly string[],
        (_e, data: any) => {
          if (!data || data.senderType !== 'organizer') return;
          if (data.bookingId && data.bookingId !== bookingId) return;
          if (showCallRef.current) return; // the guest is looking at the thread
          setCallUnread((c) => c + 1);
        }
      );
    } catch { /* realtime is best-effort */ }
    return () => { try { unsub?.(); } catch { /* noop */ } };
  }, [userId, phase, bookingId]);

  const retry = () => setAttempt((a) => a + 1);

  // Table Chat unread badge while the sheet is closed: poll the roster + refresh on a realtime ping.
  useEffect(() => {
    if (!tableChatOn || !bookingId || phase !== 'ready' || showTableChat) return;
    let alive = true;
    const refresh = () => apiService.tableChatRoster(bookingId)
      .then((r: any) => { if (!alive) return; const d = r?.data; if (d) setTableChatUnread((d.groupUnread || 0) + (d.mates || []).reduce((a: number, m: any) => a + (m.unread || 0), 0)); })
      .catch(() => {});
    refresh();
    const iv = setInterval(refresh, 15000);
    let unsub = () => {};
    try { unsub = subscribeToChannel(`user:${userId}`, ['table_chat:dm_ping'], () => refresh()); } catch { /* best-effort */ }
    return () => { alive = false; clearInterval(iv); try { unsub(); } catch { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableChatOn, bookingId, phase, showTableChat, userId]);

  const openNameEditor = () => { setNameInput(guestName); setEditingName(true); };
  const saveName = async () => {
    setSavingName(true);
    try {
      const res = await apiService.updateQrProfile(nameInput.trim());
      const saved = res?.data?.displayName;
      const finalName = (saved && saved !== 'Guest' ? saved : nameInput.trim());
      setGuestName(finalName && finalName !== 'Guest' ? finalName : '');
      setEditingName(false);
      // The QR session user resets on every new scan, so also persist the name on the guest's REGULAR
      // record — otherwise the name is forgotten next visit. remember() updates the name in place.
      if (regular && bookingId && finalName && finalName !== 'Guest') {
        try {
          const r = await apiService.rememberAsRegular(bookingId, finalName);
          const data = r?.data;
          if (data?.token && branchId) saveRegularToken(branchId, data.token);
          setRegular((prev) => (prev ? { ...prev, name: data?.name ?? finalName } : prev));
        } catch { /* best-effort — the display name still updated for this session */ }
      }
    } catch {
      /* keep the editor open on failure */
    } finally {
      setSavingName(false);
    }
  };

  // Opt in to be remembered as a regular of this venue → store the returned per-branch token.
  const becomeRegular = async () => {
    if (!bookingId || savingRegular) return;
    setSavingRegular(true);
    try {
      const res = await apiService.rememberAsRegular(bookingId, (guestName || nameInput || '').trim() || undefined);
      const data = res?.data;
      if (data?.token && branchId) saveRegularToken(branchId, data.token);
      setRegular({ name: data?.name ?? (guestName || null), visitCount: data?.visitCount ?? 1, usualLabel: null, usualItems: [] });
      if (data?.name && !guestName) setGuestName(data.name);
    } catch {
      /* opt-in is best-effort; leave the prompt so they can retry */
    } finally {
      setSavingRegular(false);
    }
  };

  // One-tap "order the usual" — re-order the regular's top items via the normal order pipeline.
  const orderUsual = async () => {
    if (!regular?.usualItems?.length || orderingUsual) return;
    setOrderingUsual(true);
    try {
      await apiService.createServiceOrder({
        bookingId: bookingId || undefined,
        branchId: branchId || undefined,
        items: regular.usualItems.map((i) => ({ stockItemId: i.stockItemId || null, itemName: i.itemName, quantity: i.quantity || 1 })),
      });
      setUsualDone(true);
      try { (navigator as any).vibrate?.(60); } catch { /* noop */ }
    } catch {
      /* leave the card so they can retry or open the full menu */
    } finally {
      setOrderingUsual(false);
    }
  };

  const brandLabel = organizerName || branchName || 'Table service';
  // Bottle-keep is a drinks-venue concept (bar/pub/lounge…), not a restaurant/café one. Some branches
  // have the feature flag on by default, so also gate the tile on the venue type.
  const isDrinksVenue = (() => {
    const s = `${branchType?.code || ''} ${branchType?.label || ''}`.toLowerCase();
    return /bar|pub|brew|tavern|lounge|club/.test(s);
  })();
  // The spot word for this venue type (Table / Chair / Room / Counter…), so the badge reads naturally.
  const spot = branchTypeTheme({ code: branchType?.code, label: branchType?.label }).spot || 'Table';
  const tableClean = (tableName || '').trim();
  const tableDisplay = tableClean
    ? (/\b(table|chair|seat|room|counter|spot|bay|lane)\b/i.test(tableClean) ? tableClean : `${spot} ${tableClean}`)
    : '';
  // Paytm-style hero: a big category cover banner with the venue logo card overlapping it, then the
  // venue name + location + your-table pill on white below.
  const hero = useMemo(() => {
    const ch = categoryHero(branchType);
    // The active event/offer for slides > 0 (slide 0 is always the venue banner/gradient).
    const promo = slide > 0 ? highlights[slide - 1] : null;
    const isOffer = promo ? /off|promo|deal|happy/i.test(promo.category || '') : false;
    // Build a Google Calendar "add event" deep link from the event's real start/end (integration-first:
    // link out to the calendar the guest already uses; no API key). Stored times are wall-clock, so we
    // emit floating date-times (no Z) to keep the same clock time in the guest's calendar.
    const calendarUrl = (() => {
      if (!promo?.startsAt) return null;
      const pad = (n: number) => String(n).padStart(2, '0');
      const fmt = (iso: string, allDay: boolean) => {
        const d = new Date(iso);
        const base = `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}`;
        return allDay ? base : `${base}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}`;
      };
      const allDay = !!promo.allDay;
      const start = fmt(promo.startsAt, allDay);
      let end: string;
      if (promo.endsAt) end = fmt(promo.endsAt, allDay);
      else if (allDay) { const n = new Date(promo.startsAt); n.setUTCDate(n.getUTCDate() + 1); end = fmt(n.toISOString(), true); }
      else end = fmt(new Date(new Date(promo.startsAt).getTime() + 2 * 3600 * 1000).toISOString(), false);
      const params = new URLSearchParams({
        action: 'TEMPLATE', text: promo.title || 'Event', dates: `${start}/${end}`,
        details: promo.description || '', location: [brandLabel, branchName].filter(Boolean).join(' '),
      });
      return `https://calendar.google.com/calendar/render?${params.toString()}`;
    })();
    return (
      <div style={styles.heroWrap}>
        {/* Carousel viewport: the banner (or gradient) is the base layer and defines the height; an
            event/offer panel is overlaid on top for slides > 0 — so the full banner is never cropped. */}
        <div style={styles.heroMedia}>
          {bannerImage ? (
            <div style={styles.heroBannerWrap}>
              <img src={bannerImage} alt={brandLabel} style={styles.heroBannerFull} />
              <div style={styles.heroScrim} aria-hidden />
            </div>
          ) : (
            <div style={{ ...styles.heroImg, background: ch.grad }}>
              <div style={styles.heroEmojis} aria-hidden>
                {ch.emojis.map((e, i) => (
                  <span key={i} style={{ ...styles.heroEmoji, ...(i === 1 ? styles.heroEmojiBig : {}) }}>{e}</span>
                ))}
              </div>
              <div style={styles.heroScrim} aria-hidden />
            </div>
          )}
          {promo && (
            <div style={{ ...styles.heroPromo, background: ch.grad }} key={promo.id} onClick={() => setSlide((s) => (s + 1) % slideCount)}>
              <div style={styles.heroPromoScrim} aria-hidden />
              <div style={styles.heroPromoBody}>
                <span style={{ ...styles.heroPromoChip, ...(isOffer ? styles.heroPromoChipOffer : {}) }}>
                  {isOffer ? '🎉 Offer' : "📅 What's on"}
                </span>
                <div style={styles.heroPromoTitle}>{promo.title}</div>
                {promo.when && <div style={styles.heroPromoWhen}>🕒 {promo.when}</div>}
                {promo.description && <div style={styles.heroPromoDesc}>{promo.description}</div>}
                {calendarUrl && (
                  <a
                    href={calendarUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={styles.heroPromoCal}
                  >
                    📅 Add to Calendar
                  </a>
                )}
              </div>
            </div>
          )}
          {slideCount > 1 && (
            <div style={styles.heroDots}>
              {Array.from({ length: slideCount }).map((_, i) => (
                <button
                  key={i}
                  aria-label={i === 0 ? 'Venue' : `Highlight ${i}`}
                  onClick={(e) => { e.stopPropagation(); setSlide(i); }}
                  style={{ ...styles.heroDot, ...(i === slide ? styles.heroDotActive : {}) }}
                />
              ))}
            </div>
          )}
        </div>
        {/* Below-banner strip = WHERE you are + what to do. The venue logo + name already live in the
            top bar (and on the banner art), so we don't repeat them here — just the branch + actions. */}
        <div style={styles.heroInfo}>
          {branchName
            ? <div style={styles.heroPlace}><MapPin size={15} color="#e0531f" /><span style={styles.heroPlaceText}>{branchName}</span></div>
            : <div style={styles.heroPlace}><MapPin size={15} color="#e0531f" /><span style={styles.heroPlaceText}>{brandLabel}</span></div>}
          <div style={styles.heroActions}>
            {tableDisplay && <div style={styles.tablePill}>📍 {tableDisplay}</div>}
            {branchId && bookingId && (
              <button style={styles.exploreBtn} onClick={() => setShowOrder(true)}>
                <BookOpen size={15} /> Explore Menu
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }, [organizerLogo, bannerImage, brandLabel, branchName, tableDisplay, branchType, branchId, bookingId, slide, slideCount, highlights]);

  return (
    <div className="ui-scope" style={styles.page}>
      <UiStyles />
      {/* Crash-safe: a render error in CallStaff / order / chat / preview shows a friendly retry
          fallback instead of white-screening the whole guest page. */}
      <ErrorBoundary>
      {phase === 'loading' ? (
        /* Branded, animated splash — a native-app-style loading screen while we set up the table. */
        <div style={styles.splash}>
          <div style={styles.splashLogoWrap}>
            <div style={styles.splashRing} aria-hidden />
            <div style={styles.splashLogo}>
              {organizerLogo
                ? <img src={organizerLogo} alt={brandLabel} style={styles.brandLogo} />
                : <Bell size={34} color="#fff" />}
            </div>
          </div>
          <div style={styles.splashBrand}>{brandLabel !== 'Table service' ? brandLabel : 'Chatup24'}</div>
          <div style={styles.splashMuted}>Setting up your table…</div>
          <div style={styles.dots}>
            <span style={{ ...styles.dot, animationDelay: '0ms' }} />
            <span style={{ ...styles.dot, animationDelay: '160ms' }} />
            <span style={{ ...styles.dot, animationDelay: '320ms' }} />
          </div>
        </div>
      ) : (
        <div style={styles.shell}>
          {/* Venue-first top bar: the business leads (logo + name); a bell for help. */}
          <div className="ui-scope" style={styles.topBar}>
            <div style={styles.topVenue}>
              {organizerLogo
                ? <button type="button" style={styles.topLogoBtn} onClick={() => setLogoZoom(true)} aria-label={`View ${brandLabel} logo`}>
                    <img src={organizerLogo} alt={brandLabel} style={styles.topLogo} />
                  </button>
                : <div style={styles.topLogoFallback}><UtensilsCrossed size={18} color="#e0531f" /></div>}
              <span style={styles.topVenueName}>{brandLabel}</span>
            </div>
            <button style={styles.topBell} onClick={() => { setShowCall(true); setCallUnread(0); }} aria-label="Call staff">
              <Bell size={20} />
              {callUnread > 0 && <span style={styles.topBellDot} />}
            </button>
          </div>
          {hero}

          {/* Venue-wide staff announcements (pushed to all tables). */}
          <div style={{ padding: '0 16px' }}>
            <BranchBroadcasts branchId={branchId || undefined} />
          </div>

          <div style={styles.body}>
            {phase === 'unavailable' && (
              <div style={styles.center}>
                <div style={{ ...styles.stateIcon, background: 'rgba(245,158,11,0.12)' }}><WifiOff size={30} color="#f59e0b" /></div>
                <div style={styles.title}>Not available right now</div>
                <div style={styles.muted}>{message}</div>
                <button style={styles.retryBtn} onClick={retry}><RefreshCw size={15} /> Try again</button>
              </div>
            )}

            {phase === 'error' && (
              <div style={styles.center}>
                <div style={{ ...styles.stateIcon, background: 'rgba(239,68,68,0.1)' }}><WifiOff size={30} color="#ef4444" /></div>
                <div style={styles.title}>Couldn’t start</div>
                <div style={styles.muted}>{message}</div>
                <button style={styles.retryBtn} onClick={retry}><RefreshCw size={15} /> Try again</button>
              </div>
            )}

            {phase === 'ended' && (
              <div style={styles.center}>
                <div style={{ ...styles.stateIcon, background: 'rgba(34,197,94,0.14)' }}><Check size={30} color="#22c55e" /></div>
                <div style={styles.title}>You’re checked out</div>
                <div style={styles.muted}>{message || 'This table session has ended. Please scan the table QR again to start a new one.'}</div>
              </div>
            )}

            {phase === 'ready' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* What's on right now. Shown before the guest orders — an offer they only discover on
                    the bill is a discount, not an offer. The amount is never computed here; the server
                    prices it, so what's promised and what's charged cannot drift apart. */}
                {offers.length > 0 && (
                  <div style={styles.offerBanner}>
                    <span style={styles.offerTag}>ON NOW</span>
                    <span style={styles.offerText}>
                      <strong>{offers[0].title}</strong>
                      {offers[0].discountPercentage ? ` — ${offers[0].discountPercentage}% off` : ''}
                      {offers[0].itemRules?.scope === 'category' && offers[0].itemRules?.categories?.length
                        ? ` ${offers[0].itemRules.categories.join(', ')}`
                        : ''}
                      {offers[0].endsAt ? ` · until ${offers[0].endsAt}` : ''}
                    </span>
                  </div>
                )}

                {/* Quiet "save your visit" prompt — an anonymous QR guest can log in to carry this
                    live session (order, tab, chat) onto their registered account. Kept subtle here;
                    a stronger nudge lives on the bill. Shown only while running as a guest session. */}
                {apiService.getGuestAccessToken() && (
                  <button
                    type="button"
                    onClick={() => beginGuestSessionClaim()}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                      padding: '10px 12px', borderRadius: radius.control, cursor: 'pointer',
                      border: '1px solid rgba(224,83,31,0.25)', background: 'rgba(224,83,31,0.06)', color: '#7a2f12',
                    }}
                  >
                    <User size={16} color="#e0531f" />
                    <span style={{ flex: 1, fontSize: '0.85rem' }}>
                      <strong>Log in to save this visit</strong> — keep your order, bill & rewards on your account
                    </span>
                    <ChevronRight size={16} color="#e0531f" />
                  </button>
                )}

                {/* Quick-action tiles — the scannable shortcut grid. Holds ONLY the actions that are
                    NOT on the persistent bottom tab bar (Order/Bill/Chat/Account) and NOT the primary
                    Call-staff banner — so each action lives in exactly one obvious place. */}
                <div style={styles.tilesRow}>
                  {branchId && (menuImageCount ?? 0) > 0 && (
                    <button style={styles.tile} onClick={() => setShowMenu(true)}>
                      <span style={styles.tileIcon}><BookOpen size={22} color="#FF6B35" /></span>
                      <span style={styles.tileLabel}>Menu</span>
                    </button>
                  )}
                  {tableChatOn && tableId && bookingId && userId && (
                    <button style={styles.tile} onClick={() => { setShowTableChat(true); setTableChatUnread(0); }}>
                      <span style={styles.tileIcon}><Users size={22} color="#FF6B35" /></span>
                      <span style={styles.tileLabel}>Table</span>
                    </button>
                  )}
                  {!entertainmentPaused && (
                    <button style={styles.tile} onClick={openGame}>
                      <span style={styles.tileIcon}><Gamepad2 size={22} color="#FF6B35" /></span>
                      <span style={styles.tileLabel}>Game</span>
                    </button>
                  )}
                  {branchId && (
                    <button style={styles.tile} onClick={() => setShowAbout(true)}>
                      <span style={styles.tileIcon}><Info size={22} color="#FF6B35" /></span>
                      <span style={styles.tileLabel}>About</span>
                    </button>
                  )}
                  {bookingId && ((isDrinksVenue && bottleKeepOn) || myBottles.length > 0) && (
                    <button style={styles.tile} onClick={() => setShowMyBottles(true)}>
                      <span style={styles.tileIcon}><Wine size={22} color="#FF6B35" /></span>
                      <span style={styles.tileLabel}>My bottles{myBottles.length > 0 ? ` (${myBottles.length})` : ''}</span>
                    </button>
                  )}
                </div>

                {/* Regulars: a recognised returning guest is greeted by name + offered "the usual" one tap.
                    Staff already know their regulars — so the app makes them feel MORE known, not less. */}
                {regular && (() => {
                  // Prefer the name the guest just entered; fall back to the stored regular name.
                  // Never show "Guest" — a warm nameless "Welcome back!" protects a regular's ego.
                  const clean = (n?: string | null) => (n && n.trim() && n.trim().toLowerCase() !== 'guest' ? n.trim() : '');
                  const who = clean(guestName) || clean(regular.name);
                  return (
                  <React.Fragment>
                    <div style={styles.welcomeCard}>
                      <div style={styles.welcomeTitle}>👋 Welcome back{who ? `, ${who}` : ''}!</div>
                      <div style={styles.welcomeSub}>{ordinal(regular.visitCount)} visit · you’re a regular here ⭐</div>
                    </div>
                    {/* Your usual picks — its own card with a one-tap reorder basket. */}
                    {regular.usualItems.length > 0 && (
                      usualDone ? (
                        <div style={styles.usualDone}>✅ Your usual is on the way</div>
                      ) : (
                        <button style={styles.usualCard} onClick={orderUsual} disabled={orderingUsual}>
                          <span style={styles.usualIcon} aria-hidden>🍺</span>
                          <span style={styles.usualCardBody}>
                            <span style={styles.usualKicker}>YOUR USUAL PICKS</span>
                            <span style={styles.usualCardTitle}>{regular.usualLabel || 'Your favourites'}</span>
                            <span style={styles.usualCardSub}>{orderingUsual ? 'Ordering…' : 'Quick reorder your favourites'}</span>
                          </span>
                          <span style={styles.usualBasket}><ShoppingBag size={20} color="#e0531f" /></span>
                        </button>
                      )
                    )}
                  </React.Fragment>
                  );
                })()}

                {/* The instinct-first front door — say/type what you need, AI turns it into an action.
                    Renders only when AI is available for this branch (self-gated). */}
                {branchId && bookingId && (
                  <Concierge
                    bookingId={bookingId}
                    branchId={branchId}
                    onNavigate={(cap, args) => {
                      // Deep-link hand-offs — open an app the guest is already signed into (device location).
                      if (cap === 'request_song') { setCallOpenSongs(true); setShowCall(true); return; }
                      if (cap === 'get_ride') { window.open('https://www.google.com/maps/search/?api=1&query=taxi', '_blank'); return; }
                      if (cap === 'nearby') { window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(args?.query || 'ATM')}`, '_blank'); return; }
                    }}
                  />
                )}

                {/* Optional display name — skippable; tap to add/edit anytime. */}
                {editingName ? (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      autoFocus
                      value={nameInput}
                      onChange={(e) => setNameInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveName(); }}
                      placeholder="Your name (optional)"
                      maxLength={40}
                      style={styles.nameInput}
                    />
                    <button style={styles.saveBtn} onClick={saveName} disabled={savingName}>
                      {savingName ? '…' : <Check size={18} />}
                    </button>
                  </div>
                ) : (
                  <button style={styles.nameRow} onClick={openNameEditor}>
                    <User size={16} color="#FF6B35" />
                    <span style={{ flex: 1, textAlign: 'left' }}>
                      {guestName ? guestName : 'Add your name (optional)'}
                    </span>
                    <Pencil size={14} color="#94a3b8" />
                  </button>
                )}

                {/* Live order tracking — surfaces status (Sent → Preparing → On the way → Served),
                    ETA, the running tab, an arrival ping and a one-tap "another round" re-order,
                    all glanceable on the home screen instead of buried in the order sheet. */}
                {userId && (
                  <OrderTracker
                    userId={userId}
                    bookingId={bookingId || undefined}
                    currency={currency || undefined}
                    onOrderAgain={() => setShowOrder(true)}
                  />
                )}

                {/* First-timer opt-in — remembered next time (name + "the usual"). Opt-in = privacy kept
                    for anyone who skips it; being a regular becomes a perk you choose. */}
                {!regular && bookingId && (
                  <button style={styles.regularOptIn} onClick={becomeRegular} disabled={savingRegular}>
                    <span>⭐ {savingRegular ? 'Saving…' : 'Make me a regular'}</span>
                    <span style={styles.regularOptInSub}>Save my name &amp; usual for next time</span>
                  </button>
                )}

                <div style={styles.sectionLabel}>What would you like to do?</div>

                {/* Call staff — the big, high-intent primary action. */}
                <button style={styles.callBtn} onClick={() => { setShowCall(true); setCallUnread(0); }}>
                  <span style={styles.callIcon}><Bell size={22} color="#fff" /></span>
                  <span style={{ flex: 1, textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={styles.callTitle}>Call staff</span>
                    <span style={styles.callSub}>Get help at your {(spot || 'table').toLowerCase()}</span>
                  </span>
                  {callUnread > 0 && <span style={styles.callBadge}>{callUnread > 99 ? '99+' : callUnread}</span>}
                </button>

              </div>
            )}
          </div>
        </div>
      )}

      {/* Bottom tab bar (venue-first app shell). Hidden while a full-screen flow is open. Each tab opens
          its full flow for now; dedicated routed screens land in later slices of the redesign. */}
      {phase === 'ready' && !showCall && !showChat && !showOrder && !showBill && !showMenu && !showTableChat && (
        <nav className="ui-scope" style={styles.tabBar}>
          <button style={{ ...styles.tab, ...styles.tabActive }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <HomeIcon size={20} /><span style={styles.tabLabel}>Home</span>
          </button>
          <button style={styles.tab} onClick={() => setShowOrder(true)}>
            <UtensilsCrossed size={20} /><span style={styles.tabLabel}>Order</span>
          </button>
          <button style={styles.tab} onClick={() => setShowBill(true)}>
            <ReceiptText size={20} /><span style={styles.tabLabel}>{billTotal && billTotal > 0 ? `Bill · ${billCurrency} ${Math.round(billTotal)}`.trim() : 'Bill'}</span>
          </button>
          <button style={styles.tab} onClick={() => setShowChat(true)}>
            <MessageSquare size={20} /><span style={styles.tabLabel}>Chat</span>
          </button>
          <button style={styles.tab} onClick={() => setShowAccount(true)}>
            <User size={20} /><span style={styles.tabLabel}>Account</span>
          </button>
        </nav>
      )}

      {logoZoom && organizerLogo && (
        <div style={styles.logoLightbox} onClick={() => setLogoZoom(false)} role="dialog" aria-label={`${brandLabel} logo`}>
          <button style={styles.logoLightboxClose} onClick={() => setLogoZoom(false)} aria-label="Close"><X size={22} /></button>
          <img src={organizerLogo} alt={brandLabel} style={styles.logoLightboxImg} onClick={(e) => e.stopPropagation()} />
          <div style={styles.logoLightboxName}>{brandLabel}</div>
        </div>
      )}

      {phase === 'ready' && showCall && bookingId && (
        <CallStaff bookingId={bookingId} tableName={tableName} branchType={branchType} branchId={branchId} openSongs={callOpenSongs} onSessionInvalid={reprovisionSession} onClose={() => { setShowCall(false); setCallOpenSongs(false); }} />
      )}

      {phase === 'ready' && showChat && bookingId && (
        <BookingChat
          bookingId={bookingId}
          organizerName={branchName || 'Staff'}
          branchName={branchName || ''}
          branchId={branchId || undefined}
          onClose={() => setShowChat(false)}
        />
      )}

      {phase === 'ready' && showOrder && branchId && bookingId && (
        <TableServiceOrderModal branchId={branchId} bookingId={bookingId} tableName={tableName} onClose={() => setShowOrder(false)} onViewMenu={() => setShowMenu(true)} />
      )}

      {phase === 'ready' && showBill && bookingId && (
        <BillModal bookingId={bookingId} branchCurrency={currency || undefined} tableName={tableName} onClose={() => setShowBill(false)} />
      )}

      {phase === 'ready' && showMyBottles && bookingId && (
        <MyBottlesModal bookingId={bookingId} onClose={() => setShowMyBottles(false)} onChanged={() => { apiService.getMyKeptBottles(bookingId).then((l) => setMyBottles(l || [])).catch(() => {}); }} />
      )}

      {phase === 'ready' && showAccount && (
        <div style={acct.overlay} onClick={() => setShowAccount(false)} role="dialog" aria-label="Account">
          <div style={acct.sheet} onClick={(e) => e.stopPropagation()}>
            <div style={acct.head}><span style={{ fontWeight: 800, fontSize: '1.05rem' }}>Your account</span><button style={acct.x} onClick={() => setShowAccount(false)} aria-label="Close">✕</button></div>
            <div style={acct.body}>
              <div style={acct.hi}>Hi, {cleanName(guestName) || cleanName(regular?.name) || 'Guest'} 👋</div>
              {regular && (regular.visitCount || 0) > 0 && <div style={acct.regular}>⭐ Regular here · visit #{regular.visitCount}</div>}
              <div style={acct.rows}>
                <div style={acct.row}><span style={acct.k}>Table</span><span style={acct.v}>{tableName || '—'}</span></div>
                <div style={acct.row}><span style={acct.k}>Venue</span><span style={acct.v}>{brandLabel}</span></div>
              </div>
              <button style={acct.editBtn} onClick={() => { setShowAccount(false); openNameEditor(); }}>✏️ Edit my name</button>
              {/* No action buttons here: Order/Bill/Chat live on the bottom tab bar and Call staff on
                  its primary banner — this sheet is your identity + session info, nothing duplicated. */}
              <p style={acct.note}>You're checked in as an anonymous guest — no login needed. Your session ends at checkout.</p>
            </div>
          </div>
        </div>
      )}

      {showGame && (
        <React.Suspense fallback={null}>
          <GamesHub venueName={brandLabel} bookingId={bookingId || undefined} tableId={tableId || undefined} userId={userId || undefined} onClose={closeGame} />
        </React.Suspense>
      )}

      {phase === 'ready' && showTableChat && tableId && bookingId && userId && (
        <TableChat bookingId={bookingId} tableId={tableId} userId={userId} onClose={() => setShowTableChat(false)} onUnreadChange={setTableChatUnread} />
      )}

      {/* Rendered LAST so, when opened from inside the Order modal (same z-index), it paints on top. */}
      {phase === 'ready' && showMenu && branchId && (
        <MenuImagesModal branchId={branchId} branchName={branchName || tableName} onClose={() => setShowMenu(false)} />
      )}

      {phase === 'ready' && showAbout && branchId && (
        <BranchAbout branchId={branchId} branchName={branchName || brandLabel} onClose={() => setShowAbout(false)} />
      )}
      </ErrorBoundary>

      <style>{`
        @keyframes qrspin{to{transform:rotate(360deg)}}
        @keyframes cuPulse{0%{transform:scale(1);opacity:0.5}100%{transform:scale(1.6);opacity:0}}
        @keyframes cuFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
        @keyframes cuDot{0%,80%,100%{opacity:0.25;transform:translateY(0)}40%{opacity:1;transform:translateY(-6px)}}
        @keyframes cuUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
        @keyframes qrHeroFade{from{opacity:0}to{opacity:1}}
      `}</style>
    </div>
  );
};

// White, native-app palette. Brand accent = Chatup24 orange (#FF6B35); cyan (#00D4FF) as a highlight.
const cleanName = (n?: string | null) => (n && n.trim() && n.trim().toLowerCase() !== 'guest' ? n.trim() : '');
const acct: Record<string, React.CSSProperties> = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 6000 },
  sheet: { background: '#fff', width: '100%', maxWidth: 480, borderRadius: '20px 20px 0 0', maxHeight: '85vh', overflow: 'auto', paddingBottom: 'calc(env(safe-area-inset-bottom,0px) + 12px)' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px 8px' },
  x: { width: 34, height: 34, borderRadius: 10, background: '#f1f5f9', border: 'none', fontSize: 15, cursor: 'pointer' },
  body: { padding: '4px 18px 18px' },
  hi: { fontSize: '1.3rem', fontWeight: 900, color: '#0f172a' },
  regular: { display: 'inline-block', marginTop: 8, background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', borderRadius: 9999, padding: '4px 12px', fontWeight: 800, fontSize: '0.82rem' },
  rows: { marginTop: 14, borderTop: '1px solid #f1f5f9' },
  row: { display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f1f5f9' },
  k: { color: '#64748b' }, v: { fontWeight: 700, color: '#0f172a' },
  editBtn: { marginTop: 14, width: '100%', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '11px', fontWeight: 700, cursor: 'pointer' },
  actions: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 },
  action: { background: '#FFF3EC', border: '1px solid #FFD9C2', color: '#9A3412', borderRadius: 12, padding: '12px', fontWeight: 800, cursor: 'pointer' },
  note: { color: '#94a3b8', fontSize: '0.78rem', marginTop: 14, textAlign: 'center' },
};

const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: '100dvh', background: '#eef1f5', display: 'flex', justifyContent: 'center', boxSizing: 'border-box' },
  // The app "canvas": a phone-width white column, centered on larger screens.
  shell: { width: 'min(480px, 100%)', minHeight: '100dvh', background: '#ffffff', display: 'flex', flexDirection: 'column', boxShadow: '0 0 40px rgba(15,23,42,0.06)' },

  // ---- Loading splash ----
  splash: { width: 'min(480px, 100%)', minHeight: '100dvh', background: '#ffffff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 24, boxSizing: 'border-box' },
  splashLogoWrap: { position: 'relative', width: 96, height: 96, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6 },
  splashRing: { position: 'absolute', width: 84, height: 84, borderRadius: 26, background: 'rgba(255,107,53,0.28)', animation: 'cuPulse 1.6s ease-out infinite' },
  splashLogo: { position: 'relative', width: 84, height: 84, borderRadius: 26, background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 14px 34px rgba(255,107,53,0.4)', animation: 'cuFloat 2.6s ease-in-out infinite', overflow: 'hidden' },
  splashBrand: { fontSize: '1.2rem', fontWeight: 800, color: '#0f172a', animation: 'cuUp 0.4s ease-out', textAlign: 'center', maxWidth: '90%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  splashMuted: { color: '#94a3b8', fontSize: '0.9rem' },
  dots: { display: 'flex', gap: 6, marginTop: 6 },
  dot: { width: 8, height: 8, borderRadius: '50%', background: '#FF6B35', animation: 'cuDot 1.2s ease-in-out infinite' },

  // ---- Hero (big category cover + overlapping logo card) ----
  brandLogo: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  heroWrap: { position: 'relative', background: '#fff' },
  heroImg: { position: 'relative', minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', paddingTop: 'env(safe-area-inset-top)' },
  // Uploaded banner: full width at natural aspect ratio so the whole designed image shows (no crop).
  heroBannerWrap: { position: 'relative', width: '100%', lineHeight: 0, background: '#0f172a', paddingTop: 'env(safe-area-inset-top)' },
  heroBannerFull: { width: '100%', height: 'auto', display: 'block' },
  heroEmojis: { display: 'flex', alignItems: 'center', gap: 20, opacity: 0.96, filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.18))' },
  heroEmoji: { fontSize: 46, lineHeight: 1 },
  heroEmojiBig: { fontSize: 74 },
  heroScrim: { position: 'absolute', inset: 0, background: 'linear-gradient(180deg,rgba(0,0,0,0) 55%,rgba(0,0,0,0.16) 100%)' },
  // ---- Hero carousel (venue slide + event/offer slides) ----
  heroMedia: { position: 'relative', overflow: 'hidden' },
  heroPromo: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', cursor: 'pointer', animation: 'qrHeroFade .5s ease' },
  heroPromoScrim: { position: 'absolute', inset: 0, background: 'linear-gradient(180deg,rgba(0,0,0,0.15) 0%,rgba(0,0,0,0.55) 100%)' },
  heroPromoBody: { position: 'relative', padding: '18px 18px 30px', color: '#fff', width: '100%' },
  heroPromoChip: { display: 'inline-block', fontSize: '0.7rem', fontWeight: 800, letterSpacing: 0.3, textTransform: 'uppercase', background: 'rgba(255,255,255,0.22)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff', padding: '3px 10px', borderRadius: 9999, marginBottom: 8, backdropFilter: 'blur(4px)' },
  heroPromoChipOffer: { background: 'rgba(255,193,7,0.9)', border: '1px solid rgba(255,193,7,1)', color: '#3a2a00' },
  heroPromoTitle: { fontSize: '1.35rem', fontWeight: 800, lineHeight: 1.15, textShadow: '0 2px 10px rgba(0,0,0,0.4)' },
  heroPromoWhen: { fontSize: '0.86rem', fontWeight: 700, marginTop: 5, opacity: 0.95 },
  heroPromoDesc: { fontSize: '0.82rem', fontWeight: 500, marginTop: 5, opacity: 0.92, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  heroPromoCal: { display: 'inline-block', marginTop: 10, fontSize: '0.8rem', fontWeight: 800, color: '#fff', background: 'rgba(255,255,255,0.22)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 9999, padding: '6px 14px', textDecoration: 'none', backdropFilter: 'blur(4px)' },
  heroDots: { position: 'absolute', bottom: 8, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 6, zIndex: 2 },
  heroDot: { width: 7, height: 7, borderRadius: 9999, border: 'none', padding: 0, background: 'rgba(255,255,255,0.5)', boxShadow: '0 1px 3px rgba(0,0,0,0.4)', cursor: 'pointer', transition: 'width .2s ease, background .2s ease' },
  heroDotActive: { width: 18, background: '#fff' },
  heroInfo: { position: 'relative', marginTop: -22, background: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: '18px 18px 18px', textAlign: 'center', borderBottom: '1px solid #f1ece8' },
  heroPlace: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 },
  heroPlaceText: { fontSize: '1.02rem', fontWeight: 800, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  heroLogoCard: { position: 'absolute', top: -36, left: '50%', transform: 'translateX(-50%)', width: 72, height: 72, borderRadius: 20, background: '#fff', padding: 4, boxShadow: '0 8px 24px rgba(15,23,42,0.18)', overflow: 'hidden' },
  heroLogoImg: { width: '100%', height: '100%', objectFit: 'cover', borderRadius: 16, display: 'block' },
  heroLogoFallback: { width: '100%', height: '100%', borderRadius: 16, background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  heroName: { fontSize: '1.4rem', fontWeight: 800, lineHeight: 1.15, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  heroSub: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginTop: 4 },
  heroSubText: { fontSize: '0.86rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  tablePill: { display: 'inline-flex', alignItems: 'center', maxWidth: '100%', background: 'rgba(255,107,53,0.12)', color: '#e0531f', fontWeight: 800, fontSize: '0.88rem', padding: '8px 16px', borderRadius: 9999, border: '1px solid rgba(255,107,53,0.28)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

  // ---- Body ----
  body: { flex: 1, padding: '16px 16px calc(84px + env(safe-area-inset-bottom))', animation: 'cuUp 0.35s ease-out' },
  // Bottom tab bar — venue-first app shell (Home · Order · Bill · Chat · Account).
  tabBar: { position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 0, width: 'min(480px, 100%)', boxSizing: 'border-box', display: 'flex', justifyContent: 'space-around', alignItems: 'stretch', background: '#fff', borderTop: '1px solid #eef1f5', padding: '8px 6px calc(8px + env(safe-area-inset-bottom))', zIndex: 900, boxShadow: '0 -4px 20px rgba(15,23,42,0.06)' },
  tab: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px 2px' },
  tabActive: { color: '#FF6B35' },
  tabLabel: { fontSize: '0.7rem', fontWeight: 700 },
  // Venue-first top bar.
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 16px', paddingTop: 'calc(10px + env(safe-area-inset-top))', background: '#fff' },
  topVenue: { display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 },
  topLogoBtn: { padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', lineHeight: 0, flexShrink: 0, borderRadius: radius.control },
  topLogo: { width: 34, height: 34, borderRadius: radius.control, objectFit: 'cover', flexShrink: 0, display: 'block' },
  // Tap the small top-bar logo → view it big.
  logoLightbox: { position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(10,12,20,0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 18, padding: 24, backdropFilter: 'blur(4px)' },
  logoLightboxImg: { maxWidth: 'min(78vw, 340px)', maxHeight: '60vh', width: 'auto', height: 'auto', objectFit: 'contain', borderRadius: 24, background: '#fff', padding: 14, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' },
  logoLightboxName: { color: '#fff', fontSize: '1.25rem', fontWeight: 800, textAlign: 'center' },
  logoLightboxClose: { position: 'absolute', top: 'calc(env(safe-area-inset-top,0px) + 14px)', right: 16, width: 42, height: 42, borderRadius: 9999, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  topLogoFallback: { width: 34, height: 34, borderRadius: radius.control, background: 'rgba(255,107,53,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  topVenueName: { fontWeight: 800, fontSize: '1rem', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  topBell: { position: 'relative', width: 40, height: 40, borderRadius: radius.control, background: '#f1f5f9', border: 'none', color: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 },
  topBellDot: { position: 'absolute', top: 8, right: 9, width: 8, height: 8, borderRadius: '50%', background: '#FF6B35', border: '2px solid #f1f5f9' },
  // Quick-action tiles.
  tilesRow: { display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 2, WebkitOverflowScrolling: 'touch' },
  // Live offer strip. Warm, not shouty — a venue telling you something good, not an ad.
  offerBanner: { display: 'flex', alignItems: 'center', gap: 10, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 12, padding: '10px 12px' },
  offerTag: { background: '#FF6B35', color: '#fff', borderRadius: 9999, padding: '2px 9px', fontSize: '0.64rem', fontWeight: 800, letterSpacing: '0.06em', flexShrink: 0 },
  offerText: { color: '#7c2d12', fontSize: '0.88rem', lineHeight: 1.4 },
  tile: { flex: '1 0 auto', minWidth: 72, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #eef1f5', borderRadius: radius.card, padding: '12px 8px', cursor: 'pointer', boxShadow: shadow.sm },
  tileIcon: { width: 40, height: 40, borderRadius: radius.control, background: 'rgba(255,107,53,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  tileLabel: { fontSize: '0.72rem', fontWeight: 800, color: '#0f172a' },
  // Hero: Table pill + Explore Menu CTA.
  heroActions: { display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 8, marginTop: 12 },
  exploreBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, background: brand.gradient, color: '#fff', border: 'none', borderRadius: radius.pill, padding: '8px 16px', fontWeight: 800, fontSize: '0.88rem', cursor: 'pointer' },
  // Your usual picks card.
  usualCard: { display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: '1px solid #ffe0cf', borderRadius: radius.card, padding: '12px 14px', cursor: 'pointer', textAlign: 'left', boxShadow: shadow.sm },
  usualIcon: { width: 46, height: 46, borderRadius: radius.control, background: 'rgba(255,107,53,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 },
  usualCardBody: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 },
  usualKicker: { fontSize: '0.66rem', fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: '#e0531f' },
  usualCardTitle: { fontSize: '0.98rem', fontWeight: 800, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  usualCardSub: { fontSize: '0.8rem', color: '#64748b' },
  usualBasket: { width: 44, height: 44, borderRadius: radius.control, background: 'rgba(255,107,53,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  sectionLabel: { fontSize: '0.8rem', fontWeight: 700, letterSpacing: 0.3, color: '#94a3b8', textTransform: 'uppercase', margin: '4px 2px 2px' },
  groupLabel: { fontSize: '0.72rem', fontWeight: 800, letterSpacing: 0.5, color: '#e0531f', textTransform: 'uppercase', margin: '10px 2px 2px', display: 'flex', alignItems: 'center', gap: 8 },
  center: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center', padding: '40px 12px' },
  stateIcon: { width: 60, height: 60, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  title: { fontSize: '1.15rem', fontWeight: 800, color: '#0f172a' },
  muted: { color: '#64748b', fontSize: '0.95rem', lineHeight: 1.45 },

  // ---- Primary "Call staff" ----
  callBtn: { position: 'relative', display: 'flex', alignItems: 'center', gap: 14, background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', color: '#fff', border: 'none', borderRadius: 16, padding: '16px 18px', cursor: 'pointer', boxShadow: '0 10px 24px rgba(255,107,53,0.34)', textAlign: 'left', width: '100%' },
  callIcon: { width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  callTitle: { fontSize: '1.1rem', fontWeight: 800, lineHeight: 1.1 },
  callSub: { fontSize: '0.82rem', fontWeight: 500, opacity: 0.92 },
  callBadge: { position: 'absolute', top: -6, right: -6, minWidth: 24, height: 24, padding: '0 6px', borderRadius: 12, background: '#fff', color: '#e0531f', fontSize: '0.78rem', fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.18)', border: '2px solid #FF6B35' },

  // ---- App action rows ----
  actionRow: { display: 'flex', alignItems: 'center', gap: 13, background: '#ffffff', color: '#0f172a', border: '1px solid #eef1f5', borderRadius: 16, padding: '13px 14px', cursor: 'pointer', width: '100%', textAlign: 'left', boxShadow: '0 2px 8px rgba(15,23,42,0.04)' },
  actionIcon: { width: 42, height: 42, borderRadius: 12, background: 'rgba(255,107,53,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  actionText: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 },
  actionTitle: { fontSize: '0.98rem', fontWeight: 700, color: '#0f172a' },
  actionSub: { fontSize: '0.8rem', color: '#94a3b8' },

  // ---- Name row + states ----
  nameRow: { display: 'flex', alignItems: 'center', gap: 10, background: '#fff8f5', color: '#0f172a', border: '1px dashed rgba(255,107,53,0.4)', borderRadius: radius.card, padding: '11px 14px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', width: '100%' },
  nameInput: { flex: 1, background: '#fff', color: '#0f172a', border: '1px solid #e2e8f0', borderRadius: radius.control, padding: '12px 14px', fontSize: '0.95rem', outline: 'none' },
  saveBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 48, background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', color: '#fff', border: 'none', borderRadius: radius.control, cursor: 'pointer', fontWeight: 800 },
  retryBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', color: '#0f172a', border: '1px solid #e2e8f0', borderRadius: 12, padding: '11px 18px', fontWeight: 700, cursor: 'pointer', marginTop: 6 },
  // Regulars
  welcomeCard: { display: 'flex', flexDirection: 'column', gap: 8, background: 'linear-gradient(135deg,#fff4ed,#ffe9dc)', border: '1px solid rgba(255,107,53,0.28)', borderRadius: radius.card, padding: '14px 16px' },
  welcomeTitle: { fontSize: '1.15rem', fontWeight: 800, color: '#0f172a' },
  welcomeSub: { fontSize: '0.85rem', fontWeight: 600, color: '#b45309' },
  usualBtn: { marginTop: 4, background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', color: '#fff', border: 'none', borderRadius: radius.card, padding: '13px 14px', fontWeight: 800, fontSize: '0.95rem', cursor: 'pointer', textAlign: 'center' },
  usualDone: { marginTop: 4, background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', borderRadius: radius.card, padding: '12px 14px', fontWeight: 800, fontSize: '0.92rem', textAlign: 'center' },
  regularOptIn: { display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start', background: '#fffdf7', color: '#0f172a', border: '1px dashed rgba(234,179,8,0.55)', borderRadius: radius.card, padding: '11px 14px', cursor: 'pointer', width: '100%', fontWeight: 800, fontSize: '0.92rem' },
  regularOptInSub: { fontWeight: 600, fontSize: '0.78rem', color: '#94a3b8' },
};

export default QrScanLanding;
